import { useQuery } from "@tanstack/react-query";
import ProfileService from "@/services/api/profile";
import type {
  Profile,
  ProfilesResponse,
  ProfileSortBy,
  ProfileSortOrder,
} from "@/services/api/profile";
import toast from "react-hot-toast";

export const profileKeys = {
  all: ["profiles"] as const,
  list: (
    page: number,
    limit: number,
    search?: string | null,
    sortBy?: ProfileSortBy,
    sortOrder?: ProfileSortOrder
  ) =>
    [...profileKeys.all, { page, limit, search: search ?? "", sortBy, sortOrder }] as const,
};

interface UseProfilesParams {
  page: number;
  limit: number;
  search?: string | null;
  sortBy?: ProfileSortBy;
  sortOrder?: ProfileSortOrder;
  enabled?: boolean;
}

export function useProfiles({
  page,
  limit,
  search,
  sortBy,
  sortOrder,
  enabled = true,
}: UseProfilesParams) {
  return useQuery<ProfilesResponse>({
    queryKey: profileKeys.list(page, limit, search, sortBy, sortOrder),
    queryFn: async () => {
      try {
        return await ProfileService.getProfiles({
          limit,
          offset: (page - 1) * limit,
          search,
          sortBy,
          sortOrder,
        });
      } catch (error: any) {
        toast.error(error?.message || "Failed to fetch admin accounts");
        throw error;
      }
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
}

export function useProfile(userId: string | undefined) {
  return useQuery<Profile | null>({
    queryKey: [...profileKeys.all, "detail", userId] as const,
    queryFn: async () => {
      try {
        return await ProfileService.getProfileById(userId as string);
      } catch (error: any) {
        toast.error(error?.message || "Failed to fetch user");
        throw error;
      }
    },
    enabled: Boolean(userId),
  });
}
